import xlsx from 'json-as-xlsx';

import { months } from 'utils/constants';
import { ACTIVATION } from 'utils/rest';

const { FullMonthFr, ShortMonthEn } = months;

export const isValidEmail = email => {
  // Regex that match only valid format
  // Valid Format:
  // username@domain.com
  const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // .test() return true if match
  return regexEmail.test(email);
};

/**
 * isEmptyObject
 * @param {Object} obj
 * @desc check if an object is really empty eq = {}
 * @return Boolean
 *
 */
export function isEmptyObject(obj) {
  return obj && !Object.values(obj).length;
}

export const exportXlsxFile = (data, fileName) => {
  const settings = {
    fileName, // Name of the resulting spreadsheet
    extraLength: 3, // A bigger number means that columns will be wider
    writeMode: 'writeFile', // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
    writeOptions: {} // Style options from https://github.com/SheetJS/sheetjs#writing-options
  };

  xlsx(data, settings);
};

export const shortMonthToFull = month => {
  const indexMonth = ShortMonthEn.indexOf(month);
  return FullMonthFr[indexMonth];
};

export const downloadCreative = async id => {
  // eslint-disable-next-line consistent-return
  return new Promise(async resolve => {
    const datas = await ACTIVATION.downloadCreatives(id);
    const uploadedAsset = await ACTIVATION.getUploadedAsset(id);
    if (!datas || !uploadedAsset) {
      return resolve();
    }
    // eslint-disable-next-line no-undef-init
    let file = undefined;
    const contentType = datas?.headers?.['content-type'] || '';
    const blob =
      datas.data instanceof Blob
        ? datas.data
        : new Blob([datas.data], {
            type: contentType
          });
    if (contentType.includes('image') || contentType.includes('octet-stream')) {
      const reader = new FileReader();
      reader.onload = async e => {
        file = e.target;
        file.type = contentType;
        file.blob = blob;
        file.height = uploadedAsset.height;
        file.width = uploadedAsset.width;
        file.id = id;
        resolve(file);
      };
      await reader.readAsDataURL(blob);
    } else {
      resolve();
    }
  });
};

export const getTabObjective = [
  {
    key: 'vsw',
    value: 'visites sur site web'
  },
  {
    key: 'vl',
    value: 'ventes en ligne'
  },
  {
    key: 'vem',
    value: 'visites en magasin'
  },
  {
    key: 'vo',
    value: 'ventes omnicanales'
  },
  {
    key: 'li',
    value: 'leads ou inscrits'
  },
  {
    key: 'dam',
    value: 'Downloads application mobile'
  },
  {
    key: 'vm',
    value: 'ventes en magasin'
  },
  {
    key: 'noto',
    value: 'notoriété'
  }
];

export const objectiveCorrespondance = objective => {
  const tabObjective = getTabObjective;
  const response =
    tabObjective.find(elem => elem.key === objective)?.value || null;
  return response !== null ? response : 'N/C';
};

export const serializePlatformAccount = elem => {
  return {
    name: elem.name,
    model: elem.model,
    config: {
      connexionId: elem.connexionId?.value || elem.connexionId || undefined,
      externalId: elem.externalId,
      linkConnexionId:
        elem.linkConnexionId?.value || elem.linkConnexionId || undefined,
      linkExternalId: elem?.linkExternalId || undefined
    },
    status: elem.status
  };
};

export const serializePlatformPage = elem => {
  return {
    name: elem.name,
    media: elem.media,
    config: {
      connexionId: elem.connexionId?.value || elem.connexionId || undefined,
      externalId: elem.externalId
    },
    status: elem.status
  };
};

export const removeDuplicates = array => {
  const uniqueSet = new Set(array);
  return [...uniqueSet];
};

export const activateAllRights = rights => {
  const activatedRights = {};
  Object.keys(rights).forEach(masterKey => {
    if (typeof rights[masterKey] === 'object' && rights[masterKey] !== null) {
      activatedRights[masterKey] = {};
      Object.keys(rights[masterKey]).forEach(subKey => {
        if (
          typeof rights[masterKey][subKey] === 'object' &&
          rights[masterKey][subKey] !== null
        ) {
          activatedRights[masterKey][subKey] = activateAllRights(
            rights[masterKey][subKey]
          );
        } else {
          activatedRights[masterKey][subKey] = subKey !== 'advanced';
        }
      });
    } else {
      activatedRights[masterKey] = masterKey !== 'advanced';
    }
  });
  return activatedRights;
};

export const parseCustomLabel = (group, separator, props) => {
  const sep1 = separator || '<';
  const sep2 = separator || '>';

  const values = Array.isArray(props)
    ? props.map(prop => group?.[prop]).filter(val => val)
    : [group?.customerName, group?.name, group?.key];

  return values.reduce(
    (acc, val, i) =>
      // eslint-disable-next-line no-nested-ternary
      `${acc}${i === 0 ? '' : i === 1 ? ` ${sep1} ` : ` ${sep2} `}${val}`,
    ''
  );
};

export const isValidUrl = string => {
  try {
    // eslint-disable-next-line no-new
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

export const filterAllRows = (value, rows) => {
  const toTest = value.toLowerCase();
  const filtered = rows.filter(item => {
    for (const key in item) {
      if (
        typeof item[key] === 'string' &&
        key !== 'active' &&
        item[key].toLowerCase().indexOf(toTest) !== -1
      ) {
        return true;
      }
    }
    return false;
  });
  return filtered;
};

export const formatListFieldUser = user => {
  // eslint-disable-next-line camelcase
  const username = `${user?.given_name} ${user?.family_name}`;
  return { username, email: user?.email, _id: user?._id };
};

export const hasAnyRight = (rightsObject, defautValue = false) => {
  if (!rightsObject || typeof rightsObject !== 'object') {
    return defautValue;
  }
  if (typeof rightsObject === 'boolean') {
    return rightsObject;
  }

  for (const key in rightsObject) {
    if (Object.prototype.hasOwnProperty.call(rightsObject, key)) {
      const value = rightsObject[key];
      if (typeof value === 'object') {
        if (hasAnyRight(value)) {
          return true;
        }
      } else if (value === true) {
        return true;
      }
    }
  }
  return false;
};
